import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, MicOff, Loader2, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../context/AuthContext';

import { BACKEND_URL } from "../utils/backendUrl";
const API = `${BACKEND_URL}/api`;

const VoiceAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && user && !historyLoaded) {
      loadHistory();
    }
    if (!isOpen) {
      setHistoryLoaded(false);
    }
  }, [isOpen, user]);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/voice/history/${user.id}`);
      const historyMessages = response.data.messages || [];
      if (historyMessages.length > 0) {
        setMessages(historyMessages.map(m => ({
          type: m.type,
          text: m.text,
          materials: m.materials || []
        })));
      }
      setHistoryLoaded(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('voice history load failed:', err.message);
      setHistoryLoaded(true);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/voice/history/${user.id}`);
      setMessages([]);
      toast.success('Historial eliminado');
    } catch (err) {
      toast.error('Error al eliminar el historial');
    }
  };

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await processAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      setError('No se pudo acceder al micrófono. Por favor, permite el acceso.');
      toast.error('Error al acceder al micrófono');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      if (user) {
        formData.append('user_id', user.id);
      }

      const response = await axios.post(`${API}/voice/assistant`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { user_text, response_text, materials } = response.data;

      setMessages(prev => [...prev, { type: 'user', text: user_text, materials: [] }]);
      setMessages(prev => [...prev, {
        type: 'assistant',
        text: response_text,
        materials: materials || []
      }]);

    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('processAudio failed:', err.message);
      setError('Error al procesar tu consulta. Intenta de nuevo.');
      toast.error('Error en el asistente de voz');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMaterialClick = () => {
    navigate(`/catalog`);
    setIsOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-[#1a8ccc] hover:bg-[#1575b0] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        data-testid="voice-assistant-btn"
      >
        <Mic className="w-6 h-6" />
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg bg-background max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#1a8ccc]" />
                Asistente de Voz
              </DialogTitle>
              {user && messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearHistory}
                  className="text-xs text-muted-foreground hover:text-red-500"
                  data-testid="clear-history-btn"
                >
                  Nueva conversación
                </Button>
              )}
            </div>
            <DialogDescription>
              {user
                ? `Hola, ${user.name}. Tu historial se guarda automáticamente.`
                : 'Pregúntame sobre materiales de impresión 3D'}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 min-h-[200px] max-h-[300px] pr-4">
            <div className="space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Mic className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Pulsa el botón del micrófono y pregunta algo como:</p>
                  <p className="text-sm mt-2 italic">"Necesito un material resistente al calor y flexible"</p>
                  {!user && (
                    <p className="text-xs mt-4 text-[#1a8ccc]">
                      Inicia sesión para guardar tu historial de conversaciones
                    </p>
                  )}
                </div>
              )}

              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      msg.type === 'user'
                        ? 'bg-[#1a8ccc] text-white'
                        : 'bg-muted'
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                    {msg.materials && msg.materials.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {msg.materials.map((mat, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="cursor-pointer hover:bg-background/50 text-xs"
                            onClick={() => handleMaterialClick(mat)}
                          >
                            {mat}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isProcessing && (
                <div className="flex justify-start">
                  <div className="bg-muted p-3 rounded-lg flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Procesando...</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {error && (
            <div className="text-red-500 text-sm text-center py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-center gap-4 pt-4 border-t">
            {isRecording ? (
              <Button
                size="lg"
                onClick={stopRecording}
                className="rounded-full w-16 h-16 bg-red-500 hover:bg-red-600 animate-pulse"
                data-testid="stop-recording-btn"
              >
                <MicOff className="w-6 h-6" />
              </Button>
            ) : (
              <Button
                size="lg"
                onClick={startRecording}
                disabled={isProcessing}
                className="rounded-full w-16 h-16 bg-[#1a8ccc] hover:bg-[#1575b0]"
                data-testid="start-recording-btn"
              >
                {isProcessing ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Mic className="w-6 h-6" />
                )}
              </Button>
            )}
          </div>

          <p className="text-xs text-center text-muted-foreground">
            {isRecording ? 'Grabando... Pulsa para detener' :
             isProcessing ? 'Analizando tu consulta...' :
             'Pulsa para hablar'}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VoiceAssistant;
